import { DayPilot } from "daypilot-pro-angular";
import { WorkShift } from "../../model/workShift";
import { ActivityActorEvent } from "../../model/activityActor-event";
import { AdminEnum, BusEnum, GroupType } from "../../common/enums";
import * as moment from "moment";
import { Availability } from "../../model/availability";
import { UserMapping } from "../../model/usermapping";
import { ResourceBase } from "./resourceBase";
import { ResourceGroup } from "./resourceGroup";
import { BusMapConstants } from "../../busmap/busmap.constants";

export interface IResourceDriver {
  userMappingID: string;
  parent: string;
  loggedin: boolean;
  workShift: WorkShift;
}

export class ResourceDriver extends ResourceBase implements IResourceDriver {

  static readonly WorkHoursBeforeBreak: number = 5
  static readonly BreakLength = 30;
  static readonly lockIcon = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d = "M3.75 9.75C3.75 9.35218 3.90804 8.97064 4.18934 8.68934C4.47064 8.40804 4.85218 8.25 5.25 8.25H12.75C13.1478 8.25 13.5294 8.40804 13.8107 8.68934C14.092 8.97064 14.25 9.35218 14.25 9.75V14.25C14.25 14.6478 14.092 15.0294 13.8107 15.3107C13.5294 15.592 13.1478 15.75 12.75 15.75H5.25C4.85218 15.75 4.47064 15.592 4.18934 15.3107C3.90804 15.0294 3.75 14.6478 3.75 14.25V9.75Z" fill = "#0F1A41" stroke = "#0F1A41" stroke-width="2" stroke - linecap="round" stroke - linejoin="round" /><path d="M6 8.25V5.25C6 4.45435 6.31607 3.69129 6.87868 3.12868C7.44129 2.56607 8.20435 2.25 9 2.25C9.79565 2.25 10.5587 2.56607 11.1213 3.12868C11.6839 3.69129 12 4.45435 12 5.25V8.25" stroke = "#0F1A41" stroke - width="2" stroke - linecap="round" stroke - linejoin="round" /><path d="M8.29289 12.7071C8.10536 12.5196 8 12.2652 8 12C8 11.7348 8.10536 11.4804 8.29289 11.2929C8.48043 11.1054 8.73478 11 9 11C9.26522 11 9.51957 11.1054 9.70711 11.2929C9.89464 11.4804 10 11.7348 10 12C10 12.2652 9.89464 12.5196 9.70711 12.7071C9.51957 12.8946 9.26522 13 9 13C8.73478 13 8.48043 12.8946 8.29289 12.7071Z" stroke = "white" stroke - width="2" stroke - linecap="round" stroke - linejoin="round" /></svg>';
  static readonly lockOpenIcon = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d = "M3.75 9.75C3.75 9.35218 3.90804 8.97064 4.18934 8.68934C4.47064 8.40804 4.85218 8.25 5.25 8.25H12.75C13.1478 8.25 13.5294 8.40804 13.8107 8.68934C14.092 8.97064 14.25 9.35218 14.25 9.75V14.25C14.25 14.6478 14.092 15.0294 13.8107 15.3107C13.5294 15.592 13.1478 15.75 12.75 15.75H5.25C4.85218 15.75 4.47064 15.592 4.18934 15.3107C3.90804 15.0294 3.75 14.6478 3.75 14.25V9.75Z" fill = "#659800" stroke = "#659800" stroke-width="2" stroke - linecap="round" stroke - linejoin="round" /><path d="M6 8.25V4.5C6 3.70435 6.31607 2.94129 6.87868 2.37868C7.44129 1.81607 8.20435 1.5 9 1.5C9.79565 1.5 10.5587 1.81607 11.1213 2.37868C11.6839 2.94129 12 3.70435 12 4.5" stroke = "#659800" stroke - width="2" stroke - linecap="round" stroke - linejoin="round" /><path d="M8.29289 12.7071C8.10536 12.5196 8 12.2652 8 12C8 11.7348 8.10536 11.4804 8.29289 11.2929C8.48043 11.1054 8.73478 11 9 11C9.26522 11 9.51957 11.1054 9.70711 11.2929C9.89464 11.4804 10 11.7348 10 12C10 12.2652 9.89464 12.5196 9.70711 12.7071C9.51957 12.8946 9.26522 13 9 13C8.73478 13 8.48043 12.8946 8.29289 12.7071Z" stroke = "white" stroke - width="2" stroke - linecap="round" stroke - linejoin="round" /></svg>';

  constructor(
    public id: string,
    public isGroup: boolean,
    public type: string,
    public moveDisabled: boolean,
    public html: string,
    public name: string,
    public userMappingID: string,
    public parent: string,
    public loggedin: boolean,
    public workShift: WorkShift
  ) {
    
    super(id, isGroup, type, moveDisabled, html, name, ResourceDriver.createToolTip(workShift));

  }

  private static createToolTip(workShift: WorkShift): string | undefined {

    if (workShift.availabilities.length === 0) return undefined;

    const descending = [...workShift.availabilities].sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    );

    let toolTip = "Schema\n";
    descending.forEach((av) => {
      if (av.metaExpanded.BusNumber === BusMapConstants.UnknownBusNumber || av.metaExpanded.BusNumber === "") {
        toolTip += moment(av.start).format("YYYY-MM-DD HH:mm") + "-" + moment(av.end).format("HH:mm") + "\n";
      } else {
        toolTip += moment(av.start).format("YYYY-MM-DD HH:mm") + "-" + moment(av.end).format("HH:mm") + " | Buss: " + av.metaExpanded.BusNumber + "\n";
      }
    })

    return toolTip;
  }

  public static castFromBaseToResourceDriver(resorceBase: ResourceBase): ResourceDriver {
    return resorceBase as ResourceDriver;
  }

  public static castToResourceDriver(args: DayPilot.ResourceData): ResourceDriver {
    return args as ResourceDriver;
  }


  public static getDriverFromDayPilotResourceByUserMappingId(resourceKey: string, resourceData: DayPilot.ResourceData[]): ResourceDriver | undefined {

    let id: DayPilot.ResourceId = resourceKey;
    return this.getDriverFromDayPilotResource(id, resourceData);

  }



  public static isTimeForBreak(cell: DayPilot.Cell, resourceBase: ResourceBase, activityActorEvents: ActivityActorEvent[]) {

    if (resourceBase.isGroup) return false;

    let resourceDriver = this.castFromBaseToResourceDriver(resourceBase);

    if (resourceDriver.type !== GroupType.Air && resourceDriver.type !== GroupType.Standby) {
      return false;
    }

    let cached = ResourceDriver.breakWindowCache.get(resourceDriver);
    if (!cached || cached.version !== ResourceDriver.breakCacheVersion) {
      cached = {
        version: ResourceDriver.breakCacheVersion,
        windows: this.calculateBreakWindows(resourceDriver, activityActorEvents)
      };
      ResourceDriver.breakWindowCache.set(resourceDriver, cached);
    }

    const t = cell.start.toDateLocal().getTime();
    return cached.windows.some(w => t > w.start && t < w.end && t < w.segmentEnd);
  }

  private static breakCacheVersion = 0;
  private static readonly breakWindowCache = new WeakMap<ResourceDriver, {
    version: number,
    windows: { start: number, end: number, segmentEnd: number }[]
  }>();

  // Call whenever activity events (e.g. breaks) or availabilities change
  public static invalidateBreakCache() {
    ResourceDriver.breakCacheVersion++;
  }

  public static isBreakEvent(a: ActivityActorEvent): boolean {
    if (a?.activityActor == undefined) return false;
    return a.activityActor.actor.type === BusEnum.Admin &&
      a.activityActor.actor.name === AdminEnum.Break;
  }

  // Break windows are calculated per continuous segment (a shift split at midnight is one segment)
  private static calculateBreakWindows(driver: ResourceDriver, activityActorEvents: ActivityActorEvent[]) {
    const windows: { start: number, end: number, segmentEnd: number }[] = [];
    const workShift = driver.workShift;
    const lastBreak = this.getLastBreak(driver, activityActorEvents);
    const lastBreakEnd = lastBreak ? lastBreak.end.toDateLocal().getTime() : undefined;
    const breakMs = this.BreakLength * 60000;
    const beforeBreakMs = this.WorkHoursBeforeBreak * 3600000;

    for (const segment of workShift.getContinuousSegments()) {
      const segStart = segment.start.getTime();
      const segEnd = segment.end.getTime();

      let timeInterval = segStart;
      if (lastBreakEnd !== undefined && lastBreakEnd >= segStart && lastBreakEnd <= segEnd) {
        timeInterval = lastBreakEnd;
      }

      const numberOfBreaks = Math.floor(Math.floor((segEnd - segStart) / 3600000) / this.WorkHoursBeforeBreak);

      for (let i = 1; i <= numberOfBreaks; i++) {
        const breakStart = timeInterval + beforeBreakMs;
        const breakEnd = breakStart + breakMs;
        timeInterval = breakEnd;

        if (!workShift.isWorkingByTime(breakStart, breakEnd)) continue;
        windows.push({ start: breakStart - 60000, end: breakEnd, segmentEnd: segEnd });
      }
    }

    return windows;
  }

  public static shouldCellBeHighlightedDueToWorkingResource(cell: DayPilot.Cell, resourceBase: ResourceBase) {

    if (resourceBase.isGroup) return false;

    let resourceDriver = this.castFromBaseToResourceDriver(resourceBase);

    if (resourceDriver === undefined) return false;

    let startTime: number = cell.start.toDateLocal().getTime();
    let endTime: number = cell.end.toDateLocal().getTime();

    return resourceDriver.workShift.isWorkingByTime(startTime, endTime);

  }

  public isDriverWorkingOnEventTime(eventStart: DayPilot.Date) {

    if (this.workShift.availabilities === undefined) return false;

    let matchingAvailabilities = this.workShift.availabilities.filter(a => ResourceDriver.toDayPilotDate(a.start) <= eventStart && ResourceDriver.toDayPilotDate(a.end) >= eventStart);
    if (matchingAvailabilities.length > 0) return true;

    return false;
  }

  public static setLoggedInStatusForAirsideDrivers(driverAirside: WorkShift[], userMappings: UserMapping[]) {
    driverAirside.forEach(driver => {
      let usermapping = userMappings.find(x => x.id == driver.userMappingID);
      if (usermapping !== undefined) {
        this.updateLockIconForUser(usermapping.userID, usermapping.loggedIn);
      }
    });
  }

  public static toDayPilotDate(date: Date) {
    return new DayPilot.Date(date, true);
  }

  private static getLastBreak(resourceDriver: ResourceDriver, activityActorEvents: ActivityActorEvent[]): ActivityActorEvent | null  {
    let lastBreak: ActivityActorEvent | null = null;

    // If we have a list of activityActors, check if at least one break has been given to the driver. If so, use
    // that time to calculate the time indicator for the next break
    if (activityActorEvents) {
      let breaks: ActivityActorEvent[] = activityActorEvents.filter(
        a => a.activityActor?.actor?.type != undefined &&
          a.activityActor.actor.type === BusEnum.Admin &&
          a.activityActor.actor.name === AdminEnum.Break &&
          a.resource === resourceDriver.id &&
          a.activityActor.status !== "CANCELLED");

      let sortedBreaks = (breaks && breaks.length > 1) ? this.sortByEndTimeDescFunction(breaks) : breaks;
      lastBreak = sortedBreaks ? sortedBreaks[0] : null;
    }

    return lastBreak;
  }

  private static getDriverFromDayPilotResource(id: DayPilot.ResourceId, resourceData: DayPilot.ResourceData[]): ResourceDriver | undefined {
    let resource = resourceData.filter(r => r.id === id);

    // we found a group -> return false.
    if (resource.length === 1) return undefined;

    for (let group of resourceData) {
      let resourceGroup = ResourceGroup.castToResourceGroup(group);
      if (!resourceGroup.children) continue;

      let drivers: ResourceDriver[] = resourceGroup.children.filter(d => d.id === id);

      if (!drivers || drivers.length !== 1) continue;

      return drivers[0];
    }

    return undefined;
  }

  private static sortByEndTimeDescFunction(input : ActivityActorEvent[]) {
    return input.sort((a, b) => {
      if (a.end > b.end)
        return -1;
      else if (a.end < b.end)
        return 1;
      else
        return 0;
    });
  }

  private static findAvailabilityForCell(workShift: WorkShift, cell: DayPilot.Cell): Availability | undefined {

    let foundItems = workShift.findAvailabilityForByTime(cell.start.getTime(), cell.end.getTime());

    if (foundItems === undefined || foundItems.length == 0) return undefined;

    if (foundItems.length == 1) return foundItems[0];
  }

  private static updateLockIconForUser(id: string, userLoggedIn: boolean) {
    let div = document.getElementById(id);

    if (!this.isNullOrUndefined(div)) {
      div!.innerHTML = userLoggedIn ? this.lockOpenIcon : this.lockIcon;
    }
  }

  private static isNullOrUndefined(object: HTMLElement | null) : boolean {
    if (object === null || object === undefined) return true;

    return false;
  }
}


